import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Store, ChefHat, TrendingUp, Layers, Pizza } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { ASSETS, BRAND } from "@/lib/brand";
import {
  SectionEyebrow,
  SectionHeading,
  SectionWrap,
  FadeUp,
  CtaBand,
} from "@/components/sections/Primitives";

export default function WhyPage() {
  useSeo({
    title: "Why Pizza Excellente | Brand Advantage",
    description:
      "See what makes Pizza Excellente different: authentic pizza, neighborhood convenience, efficient store design, and scalable local growth.",
    path: "/why-pizza-excellente",
    ogImage: ASSETS.burrata,
    ogImageAlt: "Why Pizza Excellente — premium ingredients and neighborhood focus",
  });

  const advantages = [
    {
      icon: MapPin,
      eyebrow: "01",
      title: "Neighborhood-first strategy",
      body:
        "Pizza Excellente focuses on residential communities instead of mall-led traffic. Target sites are within or adjacent to large apartment clusters and dense local neighborhoods, where customers want dependable quality close to home.",
    },
    {
      icon: Pizza,
      eyebrow: "02",
      title: "A differentiated product",
      body:
        "Our USP is authentic Italian-style thin-crust gourmet pizza prepared with premium ingredients — including gluten-free options. This helps the brand stand apart from conventional mass-market chains while remaining accessible to repeat local customers.",
    },
    {
      icon: Store,
      eyebrow: "03",
      title: "A scalable store model",
      body:
        "Each outlet is designed around an approximately 700 sq ft footprint with a 70% takeout / 30% dine-in mix. The format supports efficient kitchen workflow, space optimisation, customer convenience, and attractive neighborhood economics.",
    },
    {
      icon: ChefHat,
      eyebrow: "04",
      title: "A simple, focused menu",
      body:
        "The menu is standardised, easy to execute, and centred on pizza — supported by selected pasta, appetizers, sandwiches, and desserts. This supports consistency, speed of service, and stronger franchise execution.",
    },
    {
      icon: TrendingUp,
      eyebrow: "05",
      title: "Built for Tier 2 & Tier 3 growth",
      body:
        "Pizza Excellente is focused on Tier 2 and Tier 3 cities, where rising purchasing power and residential density create attractive expansion opportunities. The model combines strong local relevance with disciplined scalability.",
    },
  ];

  return (
    <div data-testid="why-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-44 pb-16 md:pb-24 bg-pe-cream overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <SectionEyebrow>Why Pizza Excellente</SectionEyebrow>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-pe-ink mt-6 leading-[1.02]">
                Built differently. Designed to <span className="italic text-pe-maroon">grow locally.</span>
              </h1>
              <p className="mt-8 text-pe-ink/70 text-lg leading-relaxed max-w-2xl">
                Pizza Excellente brings together authentic pizza, neighborhood convenience, and a store format designed for
                operational efficiency and repeat demand.
              </p>
              <a
                data-testid="why-hero-cta"
                href={BRAND.mailto}
                className="inline-flex items-center gap-2 mt-10 bg-pe-maroon text-white hover:bg-pe-maroon-deep transition-colors px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase"
              >
                Request Franchise Information <ArrowRight size={16} />
              </a>
            </div>
            <div className="md:col-span-5">
              <div className="aspect-square overflow-hidden">
                <img src={ASSETS.burrata} alt="Fresh burrata and premium Italian ingredients" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento of advantages */}
      <SectionWrap testId="why-advantages" tone="white">
        <div className="grid md:grid-cols-12 gap-px bg-pe-ink/10">
          {advantages.map((a, i) => (
            <FadeUp key={a.title} delay={i * 0.06} className={i === 0 || i === 4 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-6" : i === 3 ? "md:col-span-6" : "md:col-span-5"}>
              <div className="bg-white p-8 md:p-12 h-full">
                <div className="flex items-start gap-5">
                  <div className="font-serif text-5xl text-pe-rose leading-none">{a.eyebrow}</div>
                  <a.icon size={28} className="text-pe-maroon mt-2" strokeWidth={1.3} />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-pe-ink mt-6 leading-tight">{a.title}</h3>
                <p className="mt-5 text-pe-ink/70 leading-relaxed">{a.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </SectionWrap>

      {/* Market data band */}
      <SectionWrap testId="why-market" tone="mauve">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <FadeUp className="md:col-span-5">
            <SectionEyebrow>The market</SectionEyebrow>
            <SectionHeading className="mt-5">A category growing into neighborhoods.</SectionHeading>
            <p className="mt-8 text-pe-ink/75 text-lg leading-relaxed">
              India’s pizza market has expanded from a niche offering to a mainstream staple. Analysts attribute growth to urbanisation,
              rising disposable incomes, westernisation of diets, food-delivery apps, and QSR network expansion — especially in metros and Tier 2 cities.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-7">
            <div className="grid grid-cols-2 gap-8">
              {[
                { v: "$5.5B+", l: "Indian pizza market (2025 est.)" },
                { v: "10%", l: "5-yr projected CAGR" },
                { v: "75–80%", l: "Of sales driven by QSRs" },
                { v: "50%", l: "Thin-crust preference share" },
              ].map((s) => (
                <div key={s.l} className="border-t border-pe-ink/20 pt-5">
                  <div className="font-serif text-4xl md:text-5xl text-pe-maroon leading-none">{s.v}</div>
                  <div className="mt-3 text-[11px] tracking-[0.18em] uppercase text-pe-ink/70">{s.l}</div>
                </div>
              ))}
            </div>
            <p className="mt-10 text-sm text-pe-ink/55 italic">
              Source ranges: TechSci Research and independent industry analyses, 2025.
            </p>
          </FadeUp>
        </div>
      </SectionWrap>

      {/* For customers / For franchisees */}
      <SectionWrap testId="why-audiences" tone="ink">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <FadeUp>
            <div className="border-l-2 border-pe-orange pl-6">
              <div className="eyebrow text-pe-orange">For customers</div>
              <h3 className="font-serif text-3xl md:text-4xl text-pe-cream mt-4 leading-tight">
                Better pizza, close to home.
              </h3>
              <p className="mt-5 text-pe-cream/75 leading-relaxed">
                Premium thin-crust pizza, fresh ingredients, a welcoming café-style room, and free delivery within a 1 km radius —
                designed so quality and convenience finally arrive together.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="border-l-2 border-pe-orange pl-6">
              <div className="eyebrow text-pe-orange">For franchisees</div>
              <h3 className="font-serif text-3xl md:text-4xl text-pe-cream mt-4 leading-tight">
                A format engineered to win.
              </h3>
              <p className="mt-5 text-pe-cream/75 leading-relaxed">
                Compact 700 sq ft store, takeout-led demand, standardised menu, optimised kitchen layout, Petpooja POS integration,
                hyperlocal marketing playbooks, and supply-chain guidance for repeatable execution.
              </p>
            </div>
          </FadeUp>
        </div>
      </SectionWrap>

      <CtaBand
        testId="why-cta-band"
        eyebrow="Talk to us"
        heading="Talk to our team."
        body="Whether you're considering a franchise, evaluating a location, or sourcing partnerships — we'd love to hear from you."
        ctaLabel="Talk to Our Team"
        ctaHref={BRAND.mailto}
        secondaryLabel="See Franchise Opportunity"
        secondaryHref="/franchise-opportunity"
      />
    </div>
  );
}
